import { getCookie } from "../hooks/getCookie";
import { setCookie } from "../hooks/setCookie";
import { threeMonthsDate, sixHoursDate } from "./Dates.utils";

/**
 * Resolución de atribución a prueba de CookieYes.
 *
 * Desde el PR #604 (2026-09-10) la app arranca con Consent Mode v2 y un CMP que
 * BORRA las cookies propias de atribución (`gclid`, `gbraid`, `wbraid`, `utm_*`,
 * `entryPage`, `referrerUrl`) del dominio compartido `.life5.xx` mientras el
 * consentimiento está pendiente. La primera fila de tracking
 * (`POST /api/lead/track/v2`) se envía al crear el lead, normalmente antes de
 * que el visitante toque el banner, así que salía con todo a null y ese null se
 * convertía en el first touch del lead en el CRM.
 *
 * Aquí se resuelve cada clave con precedencia:
 *
 *   1. query string de la URL  (lo que trae el clic / el handoff web → app)
 *   2. sello en `sessionStorage` (`l5_attr_*`), que el CMP no toca
 *   3. cookie (`getCookie`, que ya incluye la cola diferida por consentimiento)
 *
 * La medición de atribución no está supeditada al consentimiento (decisión de
 * negocio de 2026-09-15): las cookies se escriben al momento y el
 * `POST /api/lead/track/v2` sale siempre con la atribución resuelta. El
 * resolutor se mantiene porque el CMP sigue borrando cookies del dominio
 * compartido y porque la URL y el sello sobreviven a ese borrado.
 *
 * En la llegada se sella TODO lo que aparezca en la URL o en las cookies, de
 * modo que un borrado posterior del CMP ya no pierde nada. Un valor sellado
 * nunca se sobrescribe con vacío.
 *
 * Al conceder el consentimiento (`cookieyes_consent_update`) las cookies se
 * reescriben desde el sello, igual que hace la landing en
 * dockerize-cms/gatsby/src/helpers/attribution.ts.
 */

export const ATTRIBUTION_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_test",
  "tid",
  "ucc",
  "entryPage",
  "referrerUrl",
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type Attribution = Partial<Record<AttributionKey, string | null>>;

/** Identificadores de clic: TTL 3 meses, como en la landing (LONG_LIVED_PARAMS). */
export const CLICK_ID_ATTRIBUTION_KEYS: readonly AttributionKey[] = [
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "ucc",
];

export const SEAL_PREFIX = "l5_attr_";
export const SESSION_ORIGIN_SEAL_KEY = `${SEAL_PREFIX}session_origin`;
export const OTP_LOGIN_ORIGIN = "otp_login";

/** `medium` que el modelo de atribución del CRM trata como toque no adquisitivo. */
export const OTP_LOGIN_UTM_SOURCE = "otp_login";
export const OTP_LOGIN_UTM_MEDIUM = "transactional";

export const CONSENT_UPDATE_EVENTS = [
  "cookieyes_consent_update",
  "cookieyes_banner_load",
];

const OTP_LOGIN_PATH_RE = /^\/login\/[^/]+/;

/**
 * `getCookie` devuelve la cadena "defined" cuando la cookie existe pero está
 * vacía; para atribución eso no es un valor.
 */
const isUsable = (value: string | null | undefined): value is string =>
  typeof value === "string" &&
  value.length > 0 &&
  value !== "null" &&
  value !== "undefined" &&
  value !== "defined";

const clean = (value: string | null | undefined): string | null =>
  isUsable(value) ? value : null;

const session = (): Storage | null => {
  try {
    return typeof window !== "undefined" && window.sessionStorage
      ? window.sessionStorage
      : null;
  } catch {
    // modo privado / almacenamiento deshabilitado
    return null;
  }
};

export const readSeal = (key: AttributionKey): string | null => {
  const store = session();
  if (!store) return null;
  try {
    return clean(store.getItem(`${SEAL_PREFIX}${key}`));
  } catch {
    return null;
  }
};

/** Sella un valor. Nunca sobrescribe lo sellado con un valor vacío. */
export const sealValue = (key: AttributionKey, value: string | null): void => {
  if (!isUsable(value)) return;
  const store = session();
  if (!store) return;
  try {
    store.setItem(`${SEAL_PREFIX}${key}`, value);
  } catch {
    // cuota / modo privado: el contexto en memoria sigue teniendo el valor
  }
};

export const sealAttribution = (values: Attribution): void => {
  ATTRIBUTION_KEYS.forEach((key) => sealValue(key, values[key] ?? null));
};

const fromQuery = (
  query: URLSearchParams | null | undefined,
  key: AttributionKey
): string | null => (query ? clean(query.get(key)) : null);

const currentQuery = (): URLSearchParams | null => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search);
};

/** Valor de una clave con la precedencia URL > sello > cookie. */
export const resolveAttributionValue = (
  key: AttributionKey,
  query?: URLSearchParams | null
): string | null =>
  fromQuery(query, key) ?? readSeal(key) ?? clean(getCookie(key));

/**
 * Atribución resuelta. `fallback` (el estado en memoria del contexto) se aplica
 * como último recurso, por detrás de la cookie.
 */
export const resolveAttribution = (
  query?: URLSearchParams | null,
  fallback: Attribution = {}
): Attribution => {
  const out: Attribution = {};
  ATTRIBUTION_KEYS.forEach((key) => {
    out[key] = resolveAttributionValue(key, query) ?? clean(fallback[key]);
  });
  return out;
};

export const hasRealAttribution = (values: Attribution): boolean =>
  CLICK_ID_ATTRIBUTION_KEYS.some((key) => isUsable(values[key])) ||
  isUsable(values.utm_source);

export const isOtpLoginPath = (pathname: string | null | undefined): boolean =>
  OTP_LOGIN_PATH_RE.test(pathname || "");

/**
 * Marca el origen de la sesión en la llegada. Se consulta más tarde, cuando el
 * usuario ya ha navegado fuera de `/login/<token>`.
 */
export const sealSessionOrigin = (pathname?: string | null): void => {
  const path =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  if (!isOtpLoginPath(path)) return;
  const store = session();
  if (!store) return;
  try {
    store.setItem(SESSION_ORIGIN_SEAL_KEY, OTP_LOGIN_ORIGIN);
  } catch {
    // idem
  }
};

export const sessionStartedAtOtpLogin = (): boolean => {
  const store = session();
  try {
    if (store?.getItem(SESSION_ORIGIN_SEAL_KEY) === OTP_LOGIN_ORIGIN) {
      return true;
    }
  } catch {
    // seguimos con la ruta actual
  }
  return typeof window !== "undefined" && isOtpLoginPath(window.location.pathname);
};

/**
 * Llegada: sella lo que traiga la URL y lo que ya hubiera en cookie, y marca el
 * origen de la sesión. Devuelve la atribución resuelta.
 */
export const captureArrivalAttribution = (
  query?: URLSearchParams | null,
  pathname?: string | null
): Attribution => {
  const q = query ?? currentQuery();
  const resolved = resolveAttribution(q);
  sealAttribution(resolved);
  sealSessionOrigin(pathname);
  return resolved;
};

/**
 * Reescribe las cookies de atribución desde el sello. Son de categoría
 * necesaria (decisión de negocio, 2026-09-15; ver consentGatedCookies), así
 * que `setCookie` las materializa en `.life5.xx` en el acto, haya banner o no,
 * y esta reescritura tras `cookieyes_consent_update` solo repone lo que el CMP
 * haya borrado. TTL de 3 meses para los click ids, por defecto (6 h) para el
 * resto, igual que la landing.
 */
export const restoreAttributionCookies = (): void => {
  ATTRIBUTION_KEYS.forEach((key) => {
    const sealed = readSeal(key);
    if (!isUsable(sealed)) return;
    if (isUsable(getCookie(key))) return; // no pisamos lo que ya está vigente
    setCookie(
      key,
      sealed,
      CLICK_ID_ATTRIBUTION_KEYS.includes(key) ? threeMonthsDate : sixHoursDate
    );
  });
};

let consentRecaptureInstalled = false;

/**
 * CookieYes borra las cookies mientras el consentimiento está pendiente y
 * dispara `cookieyes_consent_update` cuando el usuario decide: es la única
 * oportunidad de devolverlas. Mismo mecanismo que `installAttributionCapture`
 * en la landing.
 */
export const installAttributionRecapture = (): void => {
  if (typeof document === "undefined") return;
  if (consentRecaptureInstalled) return;
  consentRecaptureInstalled = true;
  CONSENT_UPDATE_EVENTS.forEach((eventName) =>
    document.addEventListener(eventName, () => {
      captureArrivalAttribution();
      restoreAttributionCookies();
    })
  );
};

/** Solo para tests. */
export const resetAttributionRecaptureForTests = (): void => {
  consentRecaptureInstalled = false;
};

/**
 * Atribución lista para `POST /api/lead/track/v2`.
 *
 * Regla de llegada por OTP: los leads creados fuera de la web (agente,
 * teléfono, WhatsApp) reciben un magic link `/login/<token>` que el cliente
 * suele abrir desde Gmail, sin cookies. Sin esto la app emitía una fila con
 * todo a null que el CRM no distingue de una visita directa y que se quedaba
 * como first touch del lead. Con `utm_medium = "transactional"` el modelo de
 * atribución la trata como toque no adquisitivo: rellena pero no sella.
 *
 * Si hay atribución real (cualquier click id o `utm_source`) se envía tal cual.
 */
export const resolveTrackingAttribution = (
  fallback: Attribution = {},
  options: { query?: URLSearchParams | null; otpLogin?: boolean } = {}
): Attribution => {
  const resolved = resolveAttribution(
    options.query === undefined ? currentQuery() : options.query,
    fallback
  );
  if (hasRealAttribution(resolved)) return resolved;

  const otpLogin = options.otpLogin ?? sessionStartedAtOtpLogin();
  if (!otpLogin) return resolved;

  return {
    ...resolved,
    utm_source: OTP_LOGIN_UTM_SOURCE,
    utm_medium: OTP_LOGIN_UTM_MEDIUM,
  };
};
